import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../../../services/apiClient';
import { API_ENDPOINTS } from '../../../constants/apiEndpoints';

export const getBranches = createAsyncThunk(
    'branches/getBranches',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Fetching branches with params:', params);

            const response = await apiClient.get(API_ENDPOINTS.BRANCHES, { params });
            console.log('Branches response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Branches retrieved successfully',
                    data: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch branches',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get branches error:', error);

            let errorMessage = 'Failed to fetch branches';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch branches';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

/**
 * Get a single branch
 */
export const getBranch = createAsyncThunk(
    'branches/getBranch',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Fetching branch:', id);

            const response = await apiClient.get(API_ENDPOINTS.BRANCH(id));
            console.log('Branch response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Branch retrieved successfully',
                    branch: data?.branch || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch branch',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get branch error:', error);

            let errorMessage = 'Failed to fetch branch';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch branch';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

/**
 * Create a new branch
 */
export const createBranch = createAsyncThunk(
    'branches/createBranch',
    async (branchData, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Creating branch with data:', branchData);

            const response = await apiClient.post(API_ENDPOINTS.CREATE_BRANCH, branchData);
            console.log('Create branch response:', response.data);

            const { status, message, data } = response.data;

            if (status === 201 || status === 200) {
                return {
                    success: true,
                    message: message || 'Branch created successfully',
                    branch: data?.branch || data || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to create branch',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Create branch error:', error);

            let errorMessage = 'Failed to create branch';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to create branch';
                
                if (serverData.errors) {
                    const errorMessages = Object.values(serverData.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

/**
 * Update a branch
 */
export const updateBranch = createAsyncThunk(
    'branches/updateBranch',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Updating branch:', id);
            console.log('Update data:', data);

            const response = await apiClient.put(API_ENDPOINTS.UPDATE_BRANCH(id), data);
            console.log('Update branch response:', response.data);

            const { status, message, data: responseData } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Branch updated successfully',
                    branch: responseData?.branch || responseData || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to update branch',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Update branch error:', error);

            let errorMessage = 'Failed to update branch';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to update branch';
                
                if (serverData.errors) {
                    const errorMessages = Object.values(serverData.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

/**
 * Delete a branch
 */
export const deleteBranch = createAsyncThunk(
    'branches/deleteBranch',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Deleting branch:', id);

            const response = await apiClient.delete(API_ENDPOINTS.DELETE_BRANCH(id));
            console.log('Delete branch response:', response.data);

            const { status, message } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Branch deleted successfully',
                    id: id
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to delete branch',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Delete branch error:', error);

            let errorMessage = 'Failed to delete branch';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to delete branch';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

/**
 * Toggle branch status (active/inactive)
 */
export const toggleBranchStatus = createAsyncThunk(
    'branches/toggleStatus',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Toggling branch status:', id);

            const response = await apiClient.post(API_ENDPOINTS.BRANCH_TOGGLE(id));
            console.log('Toggle branch response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Branch status toggled successfully',
                    id: id,
                    is_active: data?.is_active || false
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to toggle branch status',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Toggle branch status error:', error);

            let errorMessage = 'Failed to toggle branch status';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to toggle branch status';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

/**
 * Send SMS to a branch
 */
export const sendSmsToBranch = createAsyncThunk(
    'branches/sendSms',
    async ({ id, message, sender_id = null }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Sending SMS to branch:', id);

            const response = await apiClient.post(API_ENDPOINTS.BRANCH_SEND_SMS(id), {
                message,
                sender_id
            });
            console.log('Send SMS to branch response:', response.data);

            const { status, message: responseMessage, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: responseMessage || 'SMS sent to branch successfully',
                    data: data || {},
                    branchId: id
                };
            } else {
                return rejectWithValue({
                    message: responseMessage || 'Failed to send SMS to branch',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Send SMS to branch error:', error);

            let errorMessage = 'Failed to send SMS to branch';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to send SMS to branch';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

/**
 * Send bulk SMS to multiple branches
 */
export const sendBulkSmsToBranches = createAsyncThunk(
    'branches/sendBulkSms',
    async ({ branchIds, message, sender_id = null }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Sending bulk SMS to branches:', branchIds);

            const response = await apiClient.post(API_ENDPOINTS.BRANCH_BULK_SMS, {
                branch_ids: branchIds,
                message,
                sender_id
            });
            console.log('Bulk SMS response:', response.data);

            const { status, message: responseMessage, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: responseMessage || 'Bulk SMS sent successfully',
                    data: data || {}
                };
            } else {
                return rejectWithValue({
                    message: responseMessage || 'Failed to send bulk SMS',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Send bulk SMS error:', error);

            let errorMessage = 'Failed to send bulk SMS';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to send bulk SMS';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

/**
 * Get branch statistics
 */
export const getBranchStats = createAsyncThunk(
    'branches/getStats',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Fetching branch statistics...');

            const response = await apiClient.get(API_ENDPOINTS.BRANCH_STATS, { params });
            console.log('Branch stats response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Branch statistics retrieved successfully',
                    stats: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch branch statistics',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get branch stats error:', error);

            let errorMessage = 'Failed to fetch branch statistics';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch branch statistics';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

/**
 * Export branches
 */
export const exportBranches = createAsyncThunk(
    'branches/export',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.get(API_ENDPOINTS.BRANCHES_EXPORT, { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Branches exported successfully',
                    data: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to export branches',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Export branches error:', error);
            let errorMessage = 'Failed to export branches';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to export branches';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

// =============================================
// SLICE
// =============================================

const initialState = {
    branches: [],
    pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0
    },
    currentBranch: null,
    stats: null,
    isLoading: false,
    isSubmitting: false,
    error: null,
    successMessage: null,
    apiMessage: null,
};

const branchSlice = createSlice({
    name: 'branches',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearSuccessMessage: (state) => {
            state.successMessage = null;
        },
        clearApiMessage: (state) => {
            state.apiMessage = null;
        },
        clearBranchState: (state) => {
            state.branches = [];
            state.pagination = {
                current_page: 1,
                last_page: 1,
                per_page: 15,
                total: 0
            };
            state.currentBranch = null;
            state.stats = null;
            state.error = null;
            state.successMessage = null;
            state.apiMessage = null;
            state.isLoading = false;
            state.isSubmitting = false;
        },
        clearCurrentBranch: (state) => {
            state.currentBranch = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // ===== GET BRANCHES =====
            .addCase(getBranches.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getBranches.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                const responseData = payload.data || {};
                state.branches = responseData.data || [];
                state.pagination = responseData.meta || {
                    current_page: 1,
                    last_page: 1,
                    per_page: 15,
                    total: 0
                };
                state.successMessage = payload.message || 'Branches retrieved successfully';
                state.apiMessage = payload.message || 'Branches retrieved successfully';
                state.error = null;
            })
            .addCase(getBranches.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch branches';
                state.apiMessage = action.payload?.message || 'Failed to fetch branches';
                state.successMessage = null;
                state.branches = [];
                state.pagination = {
                    current_page: 1,
                    last_page: 1,
                    per_page: 15,
                    total: 0
                };
            })

            // ===== GET BRANCH =====
            .addCase(getBranch.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getBranch.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.currentBranch = payload.branch || null;
                state.successMessage = payload.message || 'Branch retrieved successfully';
                state.apiMessage = payload.message || 'Branch retrieved successfully';
                state.error = null;
            })
            .addCase(getBranch.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch branch';
                state.apiMessage = action.payload?.message || 'Failed to fetch branch';
                state.successMessage = null;
                state.currentBranch = null;
            })

            // ===== CREATE BRANCH =====
            .addCase(createBranch.pending, (state) => {
                state.isSubmitting = true;
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(createBranch.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Branch created successfully';
                state.apiMessage = payload.message || 'Branch created successfully';
                state.error = null;
                if (payload.branch) {
                    state.branches = [payload.branch, ...state.branches];
                }
            })
            .addCase(createBranch.rejected, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to create branch';
                state.apiMessage = action.payload?.message || 'Failed to create branch';
                state.successMessage = null;
            })

            // ===== UPDATE BRANCH =====
            .addCase(updateBranch.pending, (state) => {
                state.isSubmitting = true;
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(updateBranch.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Branch updated successfully';
                state.apiMessage = payload.message || 'Branch updated successfully';
                state.error = null;
                if (payload.branch) {
                    state.branches = state.branches.map(b => 
                        b.id === payload.branch.id ? payload.branch : b
                    );
                    if (state.currentBranch?.id === payload.branch.id) {
                        state.currentBranch = payload.branch;
                    }
                }
            })
            .addCase(updateBranch.rejected, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to update branch';
                state.apiMessage = action.payload?.message || 'Failed to update branch';
                state.successMessage = null;
            })

            // ===== DELETE BRANCH =====
            .addCase(deleteBranch.pending, (state) => {
                state.isSubmitting = true;
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(deleteBranch.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Branch deleted successfully';
                state.apiMessage = payload.message || 'Branch deleted successfully';
                state.error = null;
                if (payload.id) {
                    state.branches = state.branches.filter(b => b.id !== payload.id);
                    if (state.currentBranch?.id === payload.id) {
                        state.currentBranch = null;
                    }
                }
            })
            .addCase(deleteBranch.rejected, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to delete branch';
                state.apiMessage = action.payload?.message || 'Failed to delete branch';
                state.successMessage = null;
            })

            // ===== TOGGLE BRANCH STATUS =====
            .addCase(toggleBranchStatus.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(toggleBranchStatus.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Branch status toggled';
                state.apiMessage = payload.message || 'Branch status toggled';
                state.error = null;
                if (payload.id && payload.is_active !== undefined) {
                    state.branches = state.branches.map(b => 
                        b.id === payload.id ? { ...b, is_active: payload.is_active } : b
                    );
                    if (state.currentBranch?.id === payload.id) {
                        state.currentBranch = { ...state.currentBranch, is_active: payload.is_active };
                    }
                }
            })
            .addCase(toggleBranchStatus.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to toggle branch status';
                state.apiMessage = action.payload?.message || 'Failed to toggle branch status';
                state.successMessage = null;
            })

            // ===== SEND SMS TO BRANCH =====
            .addCase(sendSmsToBranch.pending, (state) => {
                state.isSubmitting = true;
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(sendSmsToBranch.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'SMS sent to branch successfully';
                state.apiMessage = payload.message || 'SMS sent to branch successfully';
                state.error = null;
                // Increment SMS count for the branch
                if (payload.branchId) {
                    state.branches = state.branches.map(b => 
                        b.id === payload.branchId ? { ...b, sms_count: (b.sms_count || 0) + 1 } : b
                    );
                    if (state.currentBranch?.id === payload.branchId) {
                        state.currentBranch = { ...state.currentBranch, sms_count: (state.currentBranch.sms_count || 0) + 1 };
                    }
                }
            })
            .addCase(sendSmsToBranch.rejected, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to send SMS to branch';
                state.apiMessage = action.payload?.message || 'Failed to send SMS to branch';
                state.successMessage = null;
            })

            // ===== SEND BULK SMS TO BRANCHES =====
            .addCase(sendBulkSmsToBranches.pending, (state) => {
                state.isSubmitting = true;
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(sendBulkSmsToBranches.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Bulk SMS sent successfully';
                state.apiMessage = payload.message || 'Bulk SMS sent successfully';
                state.error = null;
            })
            .addCase(sendBulkSmsToBranches.rejected, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to send bulk SMS';
                state.apiMessage = action.payload?.message || 'Failed to send bulk SMS';
                state.successMessage = null;
            })

            // ===== GET BRANCH STATS =====
            .addCase(getBranchStats.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getBranchStats.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.stats = payload.stats || null;
                state.successMessage = payload.message || 'Branch statistics retrieved successfully';
                state.apiMessage = payload.message || 'Branch statistics retrieved successfully';
                state.error = null;
            })
            .addCase(getBranchStats.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch branch statistics';
                state.apiMessage = action.payload?.message || 'Failed to fetch branch statistics';
                state.successMessage = null;
                state.stats = null;
            })

            // ===== EXPORT BRANCHES =====
            .addCase(exportBranches.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(exportBranches.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Branches exported successfully';
                state.apiMessage = payload.message || 'Branches exported successfully';
                state.error = null;
            })
            .addCase(exportBranches.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to export branches';
                state.apiMessage = action.payload?.message || 'Failed to export branches';
                state.successMessage = null;
            });
    },
});

export const {
    clearError,
    clearSuccessMessage,
    clearApiMessage,
    clearBranchState,
    clearCurrentBranch,
} = branchSlice.actions;

export const selectBranches = (state) => state.branches?.branches || [];
export const selectBranchPagination = (state) => state.branches?.pagination || {
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0
};
export const selectCurrentBranch = (state) => state.branches?.currentBranch || null;
export const selectBranchStats = (state) => state.branches?.stats || null;
export const selectBranchesLoading = (state) => state.branches?.isLoading || false;
export const selectBranchesSubmitting = (state) => state.branches?.isSubmitting || false;
export const selectBranchesError = (state) => state.branches?.error || null;
export const selectBranchesApiMessage = (state) => state.branches?.apiMessage || null;

export const selectBranchStatsSummary = (state) => {
    const stats = selectBranchStats(state);
    if (!stats) return null;
    return {
        total_branches: stats.total_branches || 0,
        total_members: stats.total_members || 0,
        total_sms: stats.total_sms || 0,
        active_branches: stats.active_branches || 0,
        inactive_branches: stats.inactive_branches || 0
    };
};

export default branchSlice.reducer;